import { setAcceptLanguage } from '@pequity/shared/axios/http';
import enUSMessages from '@pequity/shared/locales/core/en-US.json';
import { createLocaleSets } from '@pequity/shared/plugins/locale-sets';
import type { Locale } from '@pequity/shared/types/company';
import type { App, Plugin, WritableComputedRef } from 'vue';
import { createI18n } from 'vue-i18n';

type MessageSchema = typeof enUSMessages;

const DEFAULT_LOCALE = 'en-US';
const LOCALE_STORAGE_KEY = 'user_locale';

const i18n = createI18n<[MessageSchema], typeof DEFAULT_LOCALE>({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: {
    [DEFAULT_LOCALE]: enUSMessages,
  },
});

// Core en-US is imported above; every other core and feature file loads on demand: core when a
// language is set, a feature when a route that declares it is visited.
const { ensureFeatures, prepareLocale, supportedLocales } = createLocaleSets(
  i18n.global,
  import.meta.glob<{ default: MessageSchema }>([
    '../../../packages/shared/src/locales/*/*.json',
    '!../../../packages/shared/src/locales/core/en-US.json',
  ])
);

// Type predicate function to check if a value is a valid locale
const isLocale = (value: unknown): value is Locale => {
  return typeof value === 'string' && supportedLocales.includes(value);
};

// When setting legacy to false, the locale property is typed as a string instead of a writable computed ref
// This is a workaround to get the proper typing
const locale = i18n.global.locale as unknown as WritableComputedRef<Locale>;

const getLocaleFromLocalStorage = () => {
  const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);

  if (isLocale(savedLocale)) {
    return savedLocale;
  }

  return null;
};

const setLocaleInLocalStorage = (localeToSet: Locale) => {
  localStorage.setItem(LOCALE_STORAGE_KEY, localeToSet);
};

const WAYPOINT_LOCALE_MAP: Partial<Record<Locale, string>> = {
  'sr-YU': 'sr',
  'vi-VN': 'vi',
};

const toWaypointLocale = (i18nLocale: Locale): string =>
  WAYPOINT_LOCALE_MAP[i18nLocale] ?? i18nLocale.toLowerCase().replace('-', '_');

// Set the current locale and update the HTML lang attribute
const setI18nLanguage = (localeToSet: Locale): Locale => {
  locale.value = localeToSet;
  setAcceptLanguage(locale.value);
  document.querySelector('html')?.setAttribute('lang', locale.value);

  __SYNERG_READY__
    .then((cfg) => cfg.setLocale(toWaypointLocale(localeToSet)))
    .catch(() => console.warn(`[i18n] Waypoint does not support locale "${localeToSet}"`));

  return locale.value;
};

// Load core and every requested feature in the given locale, then switch to it unless a later call
// asked for another locale in the meantime.
const loadLocaleMessages = async (localeToLoad: Locale | null): Promise<Locale> => {
  const requestedLocale = localeToLoad || DEFAULT_LOCALE;
  const localeToSet = isLocale(requestedLocale) ? requestedLocale : DEFAULT_LOCALE;

  if (localeToSet !== requestedLocale) {
    console.warn(`Locale "${requestedLocale}" is not supported. Falling back to default locale.`);
  }

  const isStillDesired = await prepareLocale(localeToSet);
  return isStillDesired ? setI18nLanguage(localeToSet) : locale.value;
};

const I18nPlugin: Plugin = {
  install(app: App) {
    app.use(i18n);
  },
};

const tGlobal = i18n.global.t;

export {
  ensureFeatures,
  getLocaleFromLocalStorage,
  i18n,
  I18nPlugin,
  loadLocaleMessages,
  locale,
  setLocaleInLocalStorage,
  tGlobal,
};
